import { useState, useEffect, useRef } from "react";
import { useMatchingJudicialFiltersStore } from "../zustand/filters.store.js";
import { useFilterOptions } from "./useFilterOptions.js";
import { parseARtoISO, isFechaFutura } from "../../../common/utils/functions/dateUtils.js";
import { parseArgentinoToNumber, formatNumberToArgentino } from "../../../common/utils/functions/numberUtils.js";

export const useModalFilter = () => {
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
    const [resetKey, setResetKey] = useState(0);

    // Obtener estado y acciones del store
    const { fechaAltaDesde, fechaAltaHasta, fechaDebitoDesde, fechaDebitoHasta, coincidenciaDesde, coincidenciaHasta, jurisdiccion } = useMatchingJudicialFiltersStore((state) => state.filters);
    const setFilters = useMatchingJudicialFiltersStore((state) => state.setFilters);

    // Referencia a filtros aplicados
    const filtrosAplicadosRef = useRef({
        fechaAltaDesde: null,
        fechaAltaHasta: null,
        fechaDebitoDesde: null,
        fechaDebitoHasta: null,
        coincidenciaDesde: null,
        coincidenciaHasta: null,
        jurisdiccion: null
    });

    // Cargar opciones de filtros (jurisdicciones) desde hook centralizado
    const { jurisdicciones, jurisdiccionesLoading, getJurisdiccionLabel } = useFilterOptions();

    const convertirFechaParaFormulario = (fechaStr) => {
        if (!fechaStr || fechaStr.includes('/')) return fechaStr;

        const partes = fechaStr.split('-');
        if (partes.length === 3) {
            const [anio, mes, dia] = partes;
            return `${dia}/${mes}/${anio}`;
        }
        return fechaStr;
    };

    // Estados locales del formulario
    const [fechaAltaDesdeInput, setFechaAltaDesdeInput] = useState(
        fechaAltaDesde ? convertirFechaParaFormulario(fechaAltaDesde) : ""
    );
    const [fechaAltaHastaInput, setFechaAltaHastaInput] = useState(
        fechaAltaHasta ? convertirFechaParaFormulario(fechaAltaHasta) : ""
    );
    const [fechaDebitoDesdeInput, setFechaDebitoDesdeInput] = useState(
        fechaDebitoDesde ? convertirFechaParaFormulario(fechaDebitoDesde) : ""
    );
    const [fechaDebitoHastaInput, setFechaDebitoHastaInput] = useState(
        fechaDebitoHasta ? convertirFechaParaFormulario(fechaDebitoHasta) : ""
    );
    const [coincidenciaDesdeInput, setCoincidenciaDesdeInput] = useState(
        coincidenciaDesde ? formatNumberToArgentino(coincidenciaDesde) : ""
    );
    const [coincidenciaHastaInput, setCoincidenciaHastaInput] = useState(
        coincidenciaHasta ? formatNumberToArgentino(coincidenciaHasta) : ""
    );

    // Estados para Autocomplete de Jurisdicción
    const [idJurisdiccion, setIdJurisdiccion] = useState(jurisdiccion || null);
    const [jurisdiccionSearch, setJurisdiccionSearch] = useState(
        jurisdiccion ? getJurisdiccionLabel(jurisdiccion) : ""
    );

    // Estados de errores
    const [errorFechaAltaDesde, setErrorFechaAltaDesde] = useState("");
    const [errorFechaAltaHasta, setErrorFechaAltaHasta] = useState("");
    const [errorFechaDebitoDesde, setErrorFechaDebitoDesde] = useState("");
    const [errorFechaDebitoHasta, setErrorFechaDebitoHasta] = useState("");
    const [errorCoincidenciaDesde, setErrorCoincidenciaDesde] = useState("");
    const [errorCoincidenciaHasta, setErrorCoincidenciaHasta] = useState("");
    const [errorJurisdiccion, setErrorJurisdiccion] = useState("");

    // Workaround: proteger el estado durante el montaje para evitar que el Autocomplete lo anule
    const isMounting = useRef(true);
    useEffect(() => {
        const timer = window.setTimeout(() => {
            isMounting.current = false;
        }, 300);
        return () => window.clearTimeout(timer);
    }, []);

    // Sincronizar con el store
    useEffect(() => {
        const fechaAltaDesdeConvertida = fechaAltaDesde ? convertirFechaParaFormulario(fechaAltaDesde) : "";
        const fechaAltaHastaConvertida = fechaAltaHasta ? convertirFechaParaFormulario(fechaAltaHasta) : "";
        const fechaDebitoDesdeConvertida = fechaDebitoDesde ? convertirFechaParaFormulario(fechaDebitoDesde) : "";
        const fechaDebitoHastaConvertida = fechaDebitoHasta ? convertirFechaParaFormulario(fechaDebitoHasta) : "";
        const coincidenciaDesdeConvertida = coincidenciaDesde ? formatNumberToArgentino(coincidenciaDesde) : "";
        const coincidenciaHastaConvertida = coincidenciaHasta ? formatNumberToArgentino(coincidenciaHasta) : "";

        filtrosAplicadosRef.current = {
            fechaAltaDesde: fechaAltaDesdeConvertida,
            fechaAltaHasta: fechaAltaHastaConvertida,
            fechaDebitoDesde: fechaDebitoDesdeConvertida,
            fechaDebitoHasta: fechaDebitoHastaConvertida,
            coincidenciaDesde: coincidenciaDesdeConvertida,
            coincidenciaHasta: coincidenciaHastaConvertida,
            jurisdiccion
        };

        setFechaAltaDesdeInput(prev => prev !== fechaAltaDesdeConvertida ? fechaAltaDesdeConvertida : prev);
        setFechaAltaHastaInput(prev => prev !== fechaAltaHastaConvertida ? fechaAltaHastaConvertida : prev);
        setFechaDebitoDesdeInput(prev => prev !== fechaDebitoDesdeConvertida ? fechaDebitoDesdeConvertida : prev);
        setFechaDebitoHastaInput(prev => prev !== fechaDebitoHastaConvertida ? fechaDebitoHastaConvertida : prev);
        setCoincidenciaDesdeInput(prev => prev !== coincidenciaDesdeConvertida ? coincidenciaDesdeConvertida : prev);
        setCoincidenciaHastaInput(prev => prev !== coincidenciaHastaConvertida ? coincidenciaHastaConvertida : prev);
        setIdJurisdiccion(prev => prev !== jurisdiccion ? jurisdiccion : prev);
        setErrorFechaAltaDesde("");
        setErrorFechaAltaHasta("");
        setErrorFechaDebitoDesde("");
        setErrorFechaDebitoHasta("");
        setErrorCoincidenciaDesde("");
        setErrorCoincidenciaHasta("");
    }, [fechaAltaDesde, fechaAltaHasta, fechaDebitoDesde, fechaDebitoHasta, coincidenciaDesde, coincidenciaHasta, jurisdiccion]);

    // Actualizar label de jurisdicción cuando cambia la selección o los datos
    useEffect(() => {
        setJurisdiccionSearch(idJurisdiccion ? getJurisdiccionLabel(idJurisdiccion) : "");
    }, [idJurisdiccion, getJurisdiccionLabel]);

    // Sincronizar descripción del Autocomplete cuando cargan las opciones y hay un idJurisdiccion inicial
    useEffect(() => {
        if (jurisdicciones.length > 0 && idJurisdiccion) {
            const matched = jurisdicciones.find((j) => j.value === idJurisdiccion);
            if (matched && matched.label !== jurisdiccionSearch) {
                setJurisdiccionSearch(matched.label);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jurisdicciones, idJurisdiccion]);


    // Validaciones de fecha
    const esFechaValida = (fechaStr) => {
        if (!fechaStr?.length || fechaStr.length !== 10) return false;

        const partes = fechaStr.split('/');
        if (partes.length !== 3) return false;

        const [dia, mes, anio] = partes.map(p => Number.parseInt(p, 10));

        if (Number.isNaN(dia) || Number.isNaN(mes) || Number.isNaN(anio)) return false;
        if (mes < 1 || mes > 12) return false;
        if (dia < 1) return false;

        const diasPorMes = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
        const esBisiesto = (anio % 4 === 0 && anio % 100 !== 0) || (anio % 400 === 0);
        if (esBisiesto && mes === 2) {
            diasPorMes[1] = 29;
        }

        if (dia > diasPorMes[mes - 1]) return false;

        return true;
    };

    const fechaTieneErrorPropio = (fechaStr) => {
        if (fechaStr?.length !== 10) return false;

        const formatoInvalido = !esFechaValida(fechaStr);
        const esFutura = esFechaValida(fechaStr) && isFechaFutura(fechaStr);

        return formatoInvalido || esFutura;
    };

    // Handlers de fechas
    const validarFechaDesde = (value, fechaHastaInput, setErrorDesde, setErrorHasta, mensajeRango) => {
        if (value?.length !== 10) {
            setErrorDesde("");
            if (!fechaTieneErrorPropio(fechaHastaInput)) setErrorHasta("");
            return;
        }

        if (!esFechaValida(value)) {
            setErrorDesde("Ingresá una fecha válida");
            return;
        }

        if (isFechaFutura(value)) {
            setErrorDesde("La fecha no puede ser futura");
            return;
        }

        setErrorDesde("");
        if (fechaHastaInput?.length !== 10 || !esFechaValida(fechaHastaInput)) return;

        const fechaDesdeISO = parseARtoISO(value);
        const fechaHastaISO = parseARtoISO(fechaHastaInput);
        if (fechaDesdeISO && fechaHastaISO && fechaHastaISO < fechaDesdeISO) {
            setErrorHasta(mensajeRango);
        } else if (!fechaTieneErrorPropio(fechaHastaInput)) {
            setErrorHasta("");
        }
    };

    const validarFechaHasta = (value, fechaDesdeInput, setError, mensajeRango) => {
        if (value?.length !== 10) {
            setError("");
            return;
        }

        if (!esFechaValida(value)) {
            setError("Ingresá una fecha válida");
            return;
        }

        if (isFechaFutura(value)) {
            setError("La fecha no puede ser futura");
            return;
        }

        setError("");
        if (fechaDesdeInput?.length !== 10 || !esFechaValida(fechaDesdeInput)) return;

        const fechaDesdeISO = parseARtoISO(fechaDesdeInput);
        const fechaHastaISO = parseARtoISO(value);
        if (fechaDesdeISO && fechaHastaISO && fechaHastaISO < fechaDesdeISO) {
            setError(mensajeRango);
        }
    };

    const handleFechaAltaDesdeChange = (value) => {
        setFechaAltaDesdeInput(value);
        validarFechaDesde(
            value,
            fechaAltaHastaInput,
            setErrorFechaAltaDesde,
            setErrorFechaAltaHasta,
            "La \"fecha de alta hasta\" no puede ser anterior a la \"fecha de alta desde\""
        );
    };

    const handleFechaAltaHastaChange = (value) => {
        setFechaAltaHastaInput(value);
        validarFechaHasta(
            value,
            fechaAltaDesdeInput,
            setErrorFechaAltaHasta,
            "La \"fecha de alta hasta\" no puede ser anterior a la \"fecha de alta desde\""
        );
    };

    // Handlers de fechas de débito
    const handleFechaDebitoDesdeChange = (value) => {
        setFechaDebitoDesdeInput(value);
        validarFechaDesde(
            value,
            fechaDebitoHastaInput,
            setErrorFechaDebitoDesde,
            setErrorFechaDebitoHasta,
            "La \"fecha del débito hasta\" no puede ser anterior a la \"fecha del débito desde\""
        );
    };

    const handleFechaDebitoHastaChange = (value) => {
        setFechaDebitoHastaInput(value);
        validarFechaHasta(
            value,
            fechaDebitoDesdeInput,
            setErrorFechaDebitoHasta,
            "La \"fecha del débito hasta\" no puede ser anterior a la \"fecha del débito desde\""
        );
    };

    // Handlers de porcentajes
    const handleCoincidenciaDesdeChange = (value) => {
        setCoincidenciaDesdeInput(value);

        if (!value || value === "") {
            setErrorCoincidenciaDesde("");
            // Limpiar error de coincidenciaHasta si existía por rango
            if (errorCoincidenciaHasta === "La coincidencia hasta no puede ser menor que coincidencia desde") {
                setErrorCoincidenciaHasta("");
            }
            return;
        }

        const numero = parseArgentinoToNumber(value);
        if (numero === null) {
            setErrorCoincidenciaDesde("Ingresá un porcentaje válido");
        } else if (numero < 0 || numero > 100) {
            setErrorCoincidenciaDesde("El porcentaje debe estar entre 0 y 100");
        } else {
            setErrorCoincidenciaDesde("");

            // Validar rango si hay coincidenciaHasta
            if (coincidenciaHastaInput) {
                const hasta = parseArgentinoToNumber(coincidenciaHastaInput);
                if (hasta !== null && hasta < numero) {
                    setErrorCoincidenciaHasta("La coincidencia hasta no puede ser menor que coincidencia desde");
                } else {
                    setErrorCoincidenciaHasta("");
                }
            }
        }
    };

    const handleCoincidenciaHastaChange = (value) => {
        setCoincidenciaHastaInput(value);

        if (!value || value === "") {
            setErrorCoincidenciaHasta("");
            return;
        }

        const numero = parseArgentinoToNumber(value);
        if (numero === null) {
            setErrorCoincidenciaHasta("Ingresá un porcentaje válido");
        } else if (numero < 0 || numero > 100) {
            setErrorCoincidenciaHasta("El porcentaje debe estar entre 0 y 100");
        } else {
            setErrorCoincidenciaHasta("");

            // Validar rango si hay coincidenciaDesde
            if (coincidenciaDesdeInput) {
                const desde = parseArgentinoToNumber(coincidenciaDesdeInput);
                if (desde !== null && numero < desde) {
                    setErrorCoincidenciaHasta("La coincidencia hasta no puede ser menor que coincidencia desde");
                }
            }
        }
    };

    // Handler de jurisdicción
    const handleJurisdiccionChange = (option) => {
        if (option) {
            setIdJurisdiccion(option.value);
            setJurisdiccionSearch(option.label);
        }
    };

    const handleJurisdiccionBlur = () => {
        if (!idJurisdiccion) {
            if (!jurisdiccionSearch || jurisdiccionSearch.trim() === "") {
                setErrorJurisdiccion("");
            } else {
                setErrorJurisdiccion("Seleccioná una jurisdicción válida");
            }
        } else {
            setErrorJurisdiccion("");
        }
    };

    const handleAccept = () => {
        // Crear objeto con todos los filtros (null para los que no están seleccionados)
        const filtrosNuevos = {
            fechaAltaDesde: (fechaAltaDesdeInput?.length === 10 && esFechaValida(fechaAltaDesdeInput))
                ? parseARtoISO(fechaAltaDesdeInput)
                : null,
            fechaAltaHasta: (fechaAltaHastaInput?.length === 10 && esFechaValida(fechaAltaHastaInput))
                ? parseARtoISO(fechaAltaHastaInput)
                : null,
            fechaDebitoDesde: (fechaDebitoDesdeInput?.length === 10 && esFechaValida(fechaDebitoDesdeInput))
                ? parseARtoISO(fechaDebitoDesdeInput)
                : null,
            fechaDebitoHasta: (fechaDebitoHastaInput?.length === 10 && esFechaValida(fechaDebitoHastaInput))
                ? parseARtoISO(fechaDebitoHastaInput)
                : null,
            coincidenciaDesde: parseArgentinoToNumber(coincidenciaDesdeInput),
            coincidenciaHasta: parseArgentinoToNumber(coincidenciaHastaInput),
            jurisdiccion: idJurisdiccion || null
        };

        setFilters(filtrosNuevos);
        setIsFilterModalOpen(false);
    };

    const handleClear = () => {
        setFechaAltaDesdeInput("");
        setFechaAltaHastaInput("");
        setFechaDebitoDesdeInput("");
        setFechaDebitoHastaInput("");
        setCoincidenciaDesdeInput("");
        setCoincidenciaHastaInput("");
        setIdJurisdiccion(null);
        setJurisdiccionSearch("");
        setErrorFechaAltaDesde("");
        setErrorFechaAltaHasta("");
        setErrorFechaDebitoDesde("");
        setErrorFechaDebitoHasta("");
        setErrorCoincidenciaDesde("");
        setErrorCoincidenciaHasta("");
        setErrorJurisdiccion("");
        setResetKey(prev => prev + 1);
    };

    const restaurarFiltrosAplicados = () => {
        const jurisdiccionRestaurada = filtrosAplicadosRef.current.jurisdiccion;

        setFechaAltaDesdeInput(filtrosAplicadosRef.current.fechaAltaDesde);
        setFechaAltaHastaInput(filtrosAplicadosRef.current.fechaAltaHasta);
        setFechaDebitoDesdeInput(filtrosAplicadosRef.current.fechaDebitoDesde);
        setFechaDebitoHastaInput(filtrosAplicadosRef.current.fechaDebitoHasta);
        setCoincidenciaDesdeInput(filtrosAplicadosRef.current.coincidenciaDesde);
        setCoincidenciaHastaInput(filtrosAplicadosRef.current.coincidenciaHasta);
        setIdJurisdiccion(jurisdiccionRestaurada);
        setJurisdiccionSearch(jurisdiccionRestaurada ? getJurisdiccionLabel(jurisdiccionRestaurada) : "");
        setErrorFechaAltaDesde("");
        setErrorFechaAltaHasta("");
        setErrorFechaDebitoDesde("");
        setErrorFechaDebitoHasta("");
        setErrorCoincidenciaDesde("");
        setErrorCoincidenciaHasta("");
        setErrorJurisdiccion("");
    };

    return {
        // Estados
        isFilterModalOpen,
        setIsFilterModalOpen,
        resetKey,
        jurisdicciones,
        jurisdiccionesLoading,

        // Estados del formulario
        fechaAltaDesdeInput,
        fechaAltaHastaInput,
        fechaDebitoDesdeInput,
        fechaDebitoHastaInput,
        coincidenciaDesdeInput,
        coincidenciaHastaInput,
        idJurisdiccion,
        jurisdiccionSearch,
        errorFechaAltaDesde,
        errorFechaAltaHasta,
        errorFechaDebitoDesde,
        errorFechaDebitoHasta,
        errorCoincidenciaDesde,
        errorCoincidenciaHasta,
        errorJurisdiccion,

        // Handlers
        handleAccept,
        handleClear,
        handleFechaAltaDesdeChange,
        handleFechaAltaHastaChange,
        handleFechaDebitoDesdeChange,
        handleFechaDebitoHastaChange,
        handleCoincidenciaDesdeChange,
        handleCoincidenciaHastaChange,
        handleJurisdiccionChange,
        handleJurisdiccionBlur,
        setJurisdiccionSearch,
        setIdJurisdiccion,
        setErrorJurisdiccion,
        restaurarFiltrosAplicados,

        // Helper para obtener label
        getJurisdiccionLabel,

        // Ref de montaje para Autocomplete
        isMounting,
    };
};
